/**
 * PATH: apps/qa/src/pages/records-list/RecordsFilterBar.tsx
 * FILE_ID: QA-RECORDS-FILTER-BAR-001
 * PURPOSE: Search / job / status / view-mode / refresh chrome for the QA hub
 *          records list.
 * WHY: Lifted VERBATIM out of RecordsPage.tsx (471 lines) so the job filter
 *      could be added without pushing that page past the 500-line ceiling.
 *      Every pre-existing control keeps its markup and behaviour.
 */
import type { ComponentProps } from 'react'
import { Search, Filter, ChevronDown, X, RefreshCw, LayoutGrid, List, Building2 } from 'lucide-react'
import { JobsStatusFilterControl } from '@quotech/job-hub/jobs-list/filters/JobsStatusFilterControl'
import { cn } from '@/lib/utils'
import type { InspectionStatus } from '@/types/qa'
import type { InspectionDateMode } from './inspectionListFilters'
import type { JobStatusKey } from './useQaJobStatusSelection'

const textFieldClass = 'w-full sm:w-[160px] px-3 py-2.5 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all'

export function RecordsFilterBar({
  searchQuery,
  onSearchChange,
  jobFilter,
  onJobChange,
  clientFilter,
  onClientChange,
  siteFilter,
  onSiteChange,
  dateMode,
  onDateModeChange,
  withinDays,
  onWithinDaysChange,
  dateValue,
  onDateValueChange,
  statusFilter,
  onStatusChange,
  statusOptions,
  jobStatusBuckets,
  onToggleJobStatus,
  jobStatusAuthority,
  viewMode,
  onViewModeChange,
  onRefresh,
}: {
  searchQuery: string
  onSearchChange: (value: string) => void
  jobFilter: string
  onJobChange: (value: string) => void
  clientFilter: string
  onClientChange: (value: string) => void
  siteFilter: string
  onSiteChange: (value: string) => void
  dateMode: InspectionDateMode
  onDateModeChange: (value: InspectionDateMode) => void
  withinDays: number
  onWithinDaysChange: (value: number) => void
  dateValue: string
  onDateValueChange: (value: string) => void
  statusFilter: InspectionStatus | '' | 'none'
  onStatusChange: (value: InspectionStatus | '' | 'none') => void
  statusOptions: { value: InspectionStatus | '' | 'none'; label: string }[]
  jobStatusBuckets: JobStatusKey[]
  onToggleJobStatus: (key: JobStatusKey) => void
  jobStatusAuthority: ComponentProps<typeof JobsStatusFilterControl>['authority']
  viewMode: 'list' | 'grid'
  onViewModeChange: (mode: 'list' | 'grid') => void
  onRefresh: () => void
}) {
  return (
    <div className="bg-card rounded-xl shadow-sm border border-border p-4">
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search inspections or job #..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Reused from: packages/job-hub/src/jobs-list/filters/JobsStatusFilterControl.tsx (JobsStatusFilterControl). */}
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className="shrink-0 text-sm text-muted-foreground">Job status</span>
          <JobsStatusFilterControl
            authority={jobStatusAuthority}
            selected={jobStatusBuckets}
            onToggle={onToggleJobStatus}
          />
        </div>

        {/* Job filter — scopes the list to one job, the same collection the
            job hub QA tab shows for that job. */}
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Job #"
            aria-label="Filter by job number"
            value={jobFilter}
            onChange={(e) => onJobChange(e.target.value)}
            className="w-full sm:w-[140px] pl-10 pr-9 py-2.5 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
          />
          {jobFilter && (
            <button
              onClick={() => onJobChange('')}
              aria-label="Clear job filter"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="relative">
          <input
            type="text"
            placeholder="Client"
            aria-label="Filter by client"
            value={clientFilter}
            onChange={(event) => onClientChange(event.target.value)}
            className={textFieldClass}
          />
          {clientFilter && (
            <button
              type="button"
              onClick={() => onClientChange('')}
              aria-label="Clear client filter"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="relative">
          <input
            type="text"
            placeholder="Site"
            aria-label="Filter by site"
            value={siteFilter}
            onChange={(event) => onSiteChange(event.target.value)}
            className={textFieldClass}
          />
          {siteFilter && (
            <button
              type="button"
              onClick={() => onSiteChange('')}
              aria-label="Clear site filter"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="relative">
          <select
            aria-label="Filter by date"
            value={dateMode}
            onChange={(event) => onDateModeChange(event.target.value as InspectionDateMode)}
            className="appearance-none px-3 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent cursor-pointer min-w-[140px]"
          >
            <option value="any">Any time</option>
            <option value="within">Last days</option>
            <option value="before">Before</option>
            <option value="after">After</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
        {dateMode === 'within' ? (
          <input
            type="number"
            min={1}
            aria-label="Number of days"
            value={withinDays}
            onChange={(event) => onWithinDaysChange(Number(event.target.value))}
            className="w-[88px] px-3 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
          />
        ) : null}
        {dateMode === 'before' || dateMode === 'after' ? (
          <input
            type="date"
            aria-label="Date"
            value={dateValue}
            onChange={(event) => onDateValueChange(event.target.value)}
            className="px-3 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
          />
        ) : null}

        {/* Status filter */}
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value as InspectionStatus | '' | 'none')}
            className="appearance-none pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent cursor-pointer min-w-[160px]"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>

        {/* View mode toggle */}
        <div className="flex items-center gap-1 bg-muted rounded-xl p-1">
          <button
            onClick={() => onViewModeChange('list')}
            className={cn(
              'p-2 rounded-lg transition-colors',
              viewMode === 'list'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('grid')}
            className={cn(
              'p-2 rounded-lg transition-colors',
              viewMode === 'grid'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            title="Grid view"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>

        {/* Refresh */}
        <button
          onClick={onRefresh}
          className="p-2.5 rounded-xl border border-input text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
